import React, {
  forwardRef,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import selectStyles from '@lib/utils/selectStyles';
import Tooltip, { TooltipProps } from '@lib/components/Tooltip/Tooltip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import CharsRemaining from '@lib/components/CharsRemaining/CharsRemaining';
import InputTel, { InputTelValue } from '@lib/components/Input/InputTel';
import { getNumberInInputMask } from '@lib/components/Input/masks';
import { MAX_NUMBER_VALUE } from '@lib/enums/form';
import withFloatingLabel, {
  FloatingLabelProps,
} from '@lib/hocs/withFloatingLabel';
import InputComponent from '@lib/components/Input/InputComponent';
import { MaskitoOptions } from '@maskito/core';
import { ComponentsShape, InputSizes, InputTypes } from './enums';
import themeStyles from './Input.module.scss';

type ChangeEventType = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

export type BeforeValueChangeType = (event: ChangeEventType) => ChangeEventType;

export type OnCopyType = (
  e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
) => void;

export interface InputProps {
  autoComplete?: string;
  autoFocus?: boolean;
  beforeValueChange?: BeforeValueChangeType;
  className?: string;
  classes?: Record<string, string>;
  clearIcon?: string;
  components?: ComponentsShape;
  copyable?: boolean;
  'data-test-id'?: string;
  disabled?: boolean;
  disabledIcon?: string;
  elementTooltipProps?: TooltipProps;
  error?: string;
  formId?: string;
  hasBorder?: boolean;
  hasError?: boolean;
  hasErrorAsTooltip?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  inputWrapperClassName?: string;
  leftIcon?: string;
  leftIconClassName?: string;
  leftIconTooltipProps?: TooltipProps;
  mask?: MaskitoOptions;
  max?: number;
  maxLength?: number;
  min?: number;
  name?: string;
  onBlur?: () => void;
  onChange?: (event: ChangeEventType) => void;
  onClear?: (event: React.MouseEvent<HTMLElement>) => void;
  onClick?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onLeftIconClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onRightIconClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onlyCountries?: string[];
  placeholder?: string;
  precision?: number;
  readOnly?: boolean;
  resize?: boolean;
  rightIcon?: string;
  rightIconClassName?: string;
  rightIconSymbolsOutlined?: boolean;
  rightIconTooltipProps?: TooltipProps;
  size?: InputSizes;
  step?: number | string;
  type?: InputTypes;
  value?: string | number | InputTelValue;
}

interface PreparedProps {
  autoComplete?: string;
  max?: number;
  min?: number;
  onCopy?: OnCopyType;
  placeholder?: string;
  type?: string;
  precision?: number;
  step?: string | number;
  mask?: MaskitoOptions;
}

const Input = forwardRef(
  (
    props: InputProps & FloatingLabelProps,
    ref: React.ForwardedRef<HTMLInputElement | HTMLTextAreaElement | null>,
  ) => {
    const {
      autoComplete,
      autoFocus,
      beforeValueChange,
      className,
      classes,
      clearIcon = 'cancel',
      copyable,
      'data-test-id': dataTestId,
      disabled = false,
      disabledIcon = 'lock',
      elementTooltipProps,
      error,
      formId,
      hasBorder = true,
      hasError = false,
      hasErrorAsTooltip = false,
      inputMode,
      inputWrapperClassName,
      leftIcon,
      leftIconClassName,
      leftIconTooltipProps,
      mask,
      max,
      maxLength,
      min,
      name,
      onBlur,
      onChange,
      onClear,
      onClick,
      onKeyDown,
      onLeftIconClick,
      onlyCountries,
      placeholder,
      precision,
      readOnly,
      resize,
      rightIconSymbolsOutlined,
      setIsFocused,
      size = InputSizes.large,
      step,
      type = InputTypes.text,
      value = '',
    } = props;
    let {
      rightIcon,
      rightIconClassName,
      onRightIconClick,
      rightIconTooltipProps,
    } = props;
    const id = formId ? `input-${name}-${formId}` : `input-${name}`;

    const styles = selectStyles(themeStyles, classes);

    const { t } = useTranslation();
    const passwordVisibilityTimer = useRef<NodeJS.Timeout>(undefined);
    const [passwordVisibility, setPasswordVisibility] = useState(false);
    const onVisibilityToggle = () =>
      setPasswordVisibility((prevState) => !prevState);

    const [isCopied, setIsCopied] = useState(false);

    const handleCopyClick = () => {
      const textToCopy = _isNil(value) ? '' : `${value}`;
      if ('clipboard' in navigator) {
        (navigator.clipboard as Clipboard)
          .writeText(textToCopy)
          .then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 1000);
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error(err);
          });
      }
    };

    const onChangeHandler = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (onChange) {
        if (beforeValueChange) {
          const newEvent = beforeValueChange(event);
          onChange(newEvent);
        } else {
          onChange(event);
        }
      }
    };

    // The number mask is a fresh MaskitoOptions object each call; memoize it so
    // it keeps a stable identity across renders. InputComponent feeds it to
    // useMaskito, which keys an internal layout effect (with setState in its body
    // and cleanup) on the options object — a new one every render thrashes that
    // effect and can spiral into "Maximum update depth exceeded" (#185).
    const numberMask = useMemo<MaskitoOptions | undefined>(() => {
      if (type !== InputTypes.number) return undefined;
      const resolvedMin = min !== undefined ? min : -MAX_NUMBER_VALUE;
      const resolvedMax = max !== undefined ? max : MAX_NUMBER_VALUE;
      return getNumberInInputMask({
        precision,
        maxLength: `${resolvedMax}`.length,
        min: resolvedMin,
        max: resolvedMax,
        minusSign: resolvedMin < 0 ? '-' : undefined,
      });
    }, [type, precision, min, max]);

    const preparedProps: PreparedProps = { type };
    if (type === InputTypes.password) {
      preparedProps.type = passwordVisibility
        ? InputTypes.text
        : InputTypes.password;
      preparedProps.onCopy = (e) => {
        e.preventDefault();
      };
      rightIcon = passwordVisibility ? 'visibility' : 'visibility_off';
      rightIconClassName = styles.passwordInputIcon;
      onRightIconClick = onVisibilityToggle;
    } else if (type === InputTypes.number) {
      preparedProps.type = InputTypes.number;
      // TODO: use it only if possible hide native min max validations
      preparedProps.min = min !== undefined ? min : -MAX_NUMBER_VALUE;
      preparedProps.max = max !== undefined ? max : MAX_NUMBER_VALUE;
      preparedProps.placeholder = ''; // use for trigger label position via css
      preparedProps.precision = precision;
      preparedProps.step = step;
      if (!mask) {
        preparedProps.mask = numberMask;
      }
    }

    if (copyable && type !== InputTypes.password) {
      rightIcon = isCopied ? 'check' : 'content_copy';
      rightIconClassName = styles.copyInputIcon;
      onRightIconClick = handleCopyClick;
      rightIconTooltipProps = {
        body: t(isCopied ? 'copied' : 'copy'),
        children: undefined,
      };
    }

    if (autoComplete !== undefined) {
      preparedProps.autoComplete = autoComplete;
    }

    // Clear icon should be visible only when value exist and input is not disabled.
    const isClearable = onClear && value && !disabled;
    const isCharsRemainingVisible =
      !!maxLength && maxLength > 1 && !!CharsRemaining;
    const inputClassName = classNames(
      styles.input,
      {
        [styles.inputWithError]: !!hasError,
        [styles.inputWithLeftIcon]: !!leftIcon,
        [styles.inputWithRightIcon]:
          !!rightIcon || disabled || (hasErrorAsTooltip && error),
        [styles.inputClearable]: isClearable,
        [styles.inputDisabled]: disabled,
        [styles.textareaWithResize]: resize,
        [styles.inputLarge]:
          type !== InputTypes.textarea && size === InputSizes.large,
        [styles.inputMedium]:
          type !== InputTypes.textarea && size === InputSizes.medium,
        [styles.textareaLarge]:
          type === InputTypes.textarea && size === InputSizes.large && !resize,
        [styles.textareaMedium]:
          type === InputTypes.textarea && size === InputSizes.medium && !resize,
        [styles.textareaLargeWithResize]:
          type === InputTypes.textarea && size === InputSizes.large && resize,
        [styles.textareaMediumWithResize]:
          type === InputTypes.textarea && size === InputSizes.medium && resize,
        [styles.withoutBorder]: !hasBorder,
      },
      className,
    );
    const getDisabledIcon = () =>
      _isEmpty(rightIconTooltipProps) || !Tooltip ? (
        <div className={styles.disabledIconWrap}>
          <MaterialIcon icon={disabledIcon} symbolsOutlined />
        </div>
      ) : (
        <button
          type="button"
          onClick={onRightIconClick}
          className={styles.disabledIconWrap}
        >
          <Tooltip
            {...rightIconTooltipProps}
            contentClassName={styles.rightTooltipContent}
          >
            <MaterialIcon icon={disabledIcon} symbolsOutlined />
          </Tooltip>
        </button>
      );
    const getLeftIcon = () => {
      if (!leftIcon || type === InputTypes.tel) return null;
      return (
        <button
          className={classNames(styles.leftIconWrap, leftIconClassName)}
          type="button"
          onClick={onLeftIconClick}
        >
          {_isEmpty(leftIconTooltipProps) || !Tooltip ? (
            <MaterialIcon icon={leftIcon} />
          ) : (
            <Tooltip
              key="left-icon-tooltip"
              {...leftIconTooltipProps}
              contentClassName={styles.leftTooltipContent}
            >
              <MaterialIcon icon={leftIcon} />
            </Tooltip>
          )}
        </button>
      );
    };
    const getRightIcon = () => {
      if (disabled && !copyable) return getDisabledIcon();
      if (hasErrorAsTooltip && error) {
        return (
          <button
            className={classNames(styles.rightIconWrap, rightIconClassName)}
            type="button"
            onClick={onRightIconClick}
          >
            <Tooltip
              key="right-icon-tooltip"
              body={error}
              contentClassName={styles.rightTooltipContentError}
            >
              <MaterialIcon icon="error" />
            </Tooltip>
          </button>
        );
      }
      if (!rightIcon) return null;
      return (
        <button
          className={classNames(styles.rightIconWrap, rightIconClassName)}
          type="button"
          onClick={onRightIconClick}
        >
          {_isEmpty(rightIconTooltipProps) || !Tooltip ? (
            <MaterialIcon
              icon={rightIcon}
              symbolsOutlined={rightIconSymbolsOutlined}
            />
          ) : (
            <Tooltip
              key="right-icon-tooltip"
              {...rightIconTooltipProps}
              contentClassName={styles.rightTooltipContent}
            >
              <MaterialIcon
                icon={rightIcon}
                symbolsOutlined={rightIconSymbolsOutlined}
              />
            </Tooltip>
          )}
        </button>
      );
    };
    const getClearButton = () =>
      isClearable ? (
        <button
          className={styles.clearIconWrap}
          type="button"
          onClick={onClear}
        >
          <MaterialIcon icon={clearIcon} />
        </button>
      ) : null;
    const getInput = () => {
      const element = (
        <InputComponent
          autoFocus={autoFocus}
          data-test-id={dataTestId}
          disabled={disabled}
          id={id}
          name={name}
          inputClassName={inputClassName}
          inputMode={inputMode}
          mask={mask}
          maxLength={maxLength}
          onBlur={onBlur}
          onChangeHandler={onChangeHandler}
          onClick={onClick}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          readOnly={readOnly}
          ref={ref as React.RefObject<HTMLInputElement>}
          setIsFocused={setIsFocused}
          styles={styles}
          value={_isNil(value) ? '' : `${value}`}
          {...preparedProps}
        />
      );
      if (_isEmpty(elementTooltipProps) || !Tooltip) {
        return element;
      }
      return (
        <Tooltip
          key="element-tooltip"
          {...elementTooltipProps}
          contentClassName={styles.elementTooltipContent}
        >
          {element}
        </Tooltip>
      );
    };
    const getTextArea = () => {
      const element = (
        <textarea
          autoComplete="off"
          className={inputClassName}
          data-test-id={dataTestId}
          disabled={disabled}
          id={id}
          maxLength={maxLength}
          onBlur={value ? undefined : () => setIsFocused(false)}
          onChange={onChangeHandler}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          ref={ref as React.RefObject<HTMLTextAreaElement>}
          // eslint-disable-next-line react/no-unknown-property
          value={(value as string) || ''}
          {...preparedProps}
        />
      );
      if (_isEmpty(elementTooltipProps) || !Tooltip) {
        return element;
      }
      return (
        <Tooltip
          key="element-tooltip"
          {...elementTooltipProps}
          contentClassName={styles.elementTooltipContent}
        >
          {element}
        </Tooltip>
      );
    };
    const getPhoneInput = () => (
      <InputTel
        disabled={disabled}
        hasError={hasError}
        id={id}
        // @ts-ignore TODO: find a way to fix it
        onChange={onChangeHandler}
        onlyCountries={onlyCountries}
        value={value as InputTelValue}
      />
    );
    const getElement = () => {
      switch (type) {
        case InputTypes.textarea:
          return getTextArea();
        case InputTypes.tel:
          return getPhoneInput();
        case InputTypes.number:
        case InputTypes.text:
        case InputTypes.password:
        default:
          return getInput();
      }
    };

    useEffect(() => {
      if (passwordVisibility) {
        passwordVisibilityTimer.current = setTimeout(() => {
          setPasswordVisibility(false);
        }, 2000);
      }
      return () => {
        if (passwordVisibilityTimer.current) {
          clearInterval(passwordVisibilityTimer.current);
        }
      };
    }, [passwordVisibility]);

    return (
      <div className={styles.root}>
        <div
          className={classNames(
            styles.inputWrapper,
            {
              [styles.inputWrapperWithTextarea]: type === InputTypes.textarea,
              [styles.inputWrapperWithNumber]: type === InputTypes.number,
              [styles.inputWrapperWithDisabled]: disabled,
              [styles.inputWrapperWhenValue]: !!value,
              [styles.inputWrapperWhenError]: hasError,
            },
            inputWrapperClassName,
          )}
        >
          {getElement()}
          {getLeftIcon()}
          {getRightIcon()}
          {getClearButton()}
        </div>
        {isCharsRemainingVisible && (
          <CharsRemaining
            value={value as string}
            maxLength={maxLength}
            className={styles.charsRemainingClass}
          />
        )}
      </div>
    );
  },
);

export default memo(withFloatingLabel(Input));
